import { CategoryIcon } from 'components/icons/category-icon'
import { NextIcon } from 'components/icons/next'
import { SkeletonFilterSection } from 'components/loading/skeleton'
import { RasaPageComponentProps } from 'components/rasa-page-component/types'
import { Dataset } from 'generic/dataset'
import React, { useCallback, useEffect, useState } from 'react'
import { shouldShowBackButton } from '../../generic/utility'
import {
  CONTENT_LIBRARY_PAGE,
  FILTER_BY_CATEGORY_TEXT,
  GAEvents,
  PAGE_HEADER_DESCRIPTION,
  Pages,
  SELECT_CATEGORY_PAGE,
} from '../../rootConstants'
import '../_styles.scss'
import { BackButtonComponent } from '../back-button'
import { sendGAEvent } from '../utils'
import './_styles-css.css'
import { Category } from './types'

type SelectCategoryPageComponentProps = RasaPageComponentProps

export const SelectCategoryPageComponent: React.FC<SelectCategoryPageComponentProps> = ({ store, navigate }) => {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])

  useEffect(() => {
    const datasetParams = [{ param: 'limit', value: 8 }] // Limit to original 8 categories
    new Dataset()
      .loadPublicDataset('categories', datasetParams)
      .then(result => {
        setCategories(result[0])
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [])

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCategoryClick = useCallback(
    (category: Category) => {
      store.dispatch(
        (c => ({ type: CONTENT_LIBRARY_PAGE, payload: c }))({
          selectedCategory: category,
          senderPage: SELECT_CATEGORY_PAGE,
        })
      )
      sendGAEvent(store.getState(), { action: GAEvents.Page2CategorySelect, label: category.name })
      navigate(Pages.contentLibrary)
    },
    [store, navigate]
  )

  const handleMultiSelect = useCallback((category: Category) => {
    setSelectedCategories(prev => {
      const isSelected = prev.some(c => c.id === category.id)
      if (isSelected) {
        return prev.filter(c => c.id !== category.id)
      } else {
        return [...prev, category]
      }
    })
  }, [])

  const handleProceedWithMultiple = useCallback(() => {
    if (selectedCategories.length > 0) {
      const payload = {
        selectedCategories: selectedCategories.map(c => c.name),
        selectedCategory: selectedCategories[0], // Keep for backward compatibility
        senderPage: SELECT_CATEGORY_PAGE,
      }
      store.dispatch((c => ({ type: CONTENT_LIBRARY_PAGE, payload: c }))(payload))
      sendGAEvent(store.getState(), {
        action: GAEvents.Page2CategorySelect,
        label: selectedCategories.map(c => c.name).join(', '),
      })
      navigate(Pages.contentLibrary)
    }
  }, [selectedCategories, store, navigate])

  const handleSkip = useCallback(() => {
    store.dispatch(
      (x => ({ type: CONTENT_LIBRARY_PAGE, payload: x }))({
        searchTerm: '',
        selectedCategory: {},
        senderPage: SELECT_CATEGORY_PAGE,
      })
    )
    navigate(Pages.contentLibrary)
  }, [store, navigate])

  const handleGoBack = useCallback(() => {
    navigate(Pages.search)
  }, [navigate])

  return (
    <div className="modern-select-category-page">
      <div className="modern-select-category-page__container">
        {/* Back Button */}
        {shouldShowBackButton() && (
          <div className="modern-select-category-page__back">
            <BackButtonComponent onClick={handleGoBack} />
          </div>
        )}

        {/* Header */}
        <header className="modern-select-category-page__header">
          <div className="modern-select-category-page__icon">
            <CategoryIcon />
          </div>
          <h1 className="modern-select-category-page__title">{FILTER_BY_CATEGORY_TEXT}</h1>
          <p className="modern-select-category-page__description">{PAGE_HEADER_DESCRIPTION}</p>
        </header>

        {/* Search Categories */}
        <div className="modern-select-category-page__search">
          <div className="modern-select-category-page__search-container">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="modern-select-category-page__search-input"
            />
            <div className="modern-select-category-page__search-icon">🔍</div>
          </div>
        </div>

        {/* Multi-select Toggle */}
        <div className="modern-select-category-page__controls">
          <div className="modern-select-category-page__mode-info">
            <span className="modern-select-category-page__mode-text">
              Click a category to browse, or select multiple categories
            </span>
          </div>
          {selectedCategories.length > 0 && (
            <div className="modern-select-category-page__selection-actions">
              <span className="modern-select-category-page__selection-count">{selectedCategories.length} selected</span>
              <button onClick={handleProceedWithMultiple} className="modern-select-category-page__proceed-btn">
                Continue with Selection
              </button>
              <button onClick={() => setSelectedCategories([])} className="modern-select-category-page__clear-btn">
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Categories Grid */}
        <div className="modern-select-category-page__content">
          {isLoading ? (
            <div className="modern-select-category-page__loading">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonFilterSection key={index} />
              ))}
            </div>
          ) : (
            <div className="modern-select-category-page__grid">
              {filteredCategories.map((category, index) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  index={index}
                  isSelected={selectedCategories.some(c => c.id === category.id)}
                  onClick={handleCategoryClick}
                  onMultiSelect={handleMultiSelect}
                />
              ))}
              {filteredCategories.length === 0 && searchTerm && (
                <div className="modern-select-category-page__no-results">
                  <div className="modern-select-category-page__no-results-icon">🔍</div>
                  <h3 className="modern-select-category-page__no-results-title">No categories found</h3>
                  <p className="modern-select-category-page__no-results-text">
                    Try adjusting your search term or browse all categories
                  </p>
                  <button onClick={() => setSearchTerm('')} className="modern-select-category-page__clear-search-btn">
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Skip Option */}
        <div className="modern-select-category-page__footer">
          <button onClick={handleSkip} className="modern-select-category-page__skip-btn">
            Skip and browse all sources
          </button>
        </div>
      </div>
    </div>
  )
}

interface CategoryCardProps {
  category: Category
  index: number
  isSelected: boolean
  onClick: (category: Category) => void
  onMultiSelect: (category: Category) => void
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, index, isSelected, onClick, onMultiSelect }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        e.preventDefault()
        onMultiSelect(category)
      } else {
        onClick(category)
      }
    },
    [category, onClick, onMultiSelect]
  )

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onMultiSelect(category)
    },
    [category, onMultiSelect]
  )

  return (
    <div
      className={`modern-category-card ${isSelected ? 'modern-category-card--selected' : ''}`}
      onClick={handleClick}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="modern-category-card__header">
        <div className="modern-category-card__checkbox-container">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {
              // Controlled by onClick handler
            }}
            onClick={handleCheckboxClick}
            className="modern-category-card__checkbox"
            aria-label={`Select ${category.name} category`}
          />
        </div>
        <div className="modern-category-card__icon">📁</div>
      </div>

      <div className="modern-category-card__content">
        <h3 className="modern-category-card__name">{category.name}</h3>
        <p className="modern-category-card__description">Explore sources related to {category.name.toLowerCase()}</p>
        <div className="modern-category-card__meta">
          <span className="modern-category-card__rank">Rank {category.rank}</span>
        </div>
      </div>

      <div className="modern-category-card__action">
        <NextIcon />
      </div>

      <div className="modern-category-card__preview">
        <div className="modern-category-card__preview-text">Click to browse {category.name} sources</div>
      </div>
    </div>
  )
}
